import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { horseAPI, favoriteAPI, breedsAPI, publicAPI, coatsAPI } from '@/services/api';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { createTranslationMap } from '@/utils/i18nHelpers';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import ImageViewer from "@/components/ImageViewer";
import VideoViewer from "@/components/VideoViewer";
import BackLink from "@/components/BackLink";
import LocationViewer from "@/components/LocationViewer";
import StickyInfoCard from "@/components/StickyInfoCard";

// Fix leaflet default icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

export default function HorseDetailPage() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [horse, setHorse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFav, setIsFav] = useState(false);
  const [breedMap, setBreedMap] = useState({});
	const [coatMap, setCoatMap] = useState({});
	const [pricing, setPricing] = useState({
		free_images_limit: 5, price_extra_image: 0.70,
		free_videos_limit: 1, price_extra_video: 3.00,
		max_video_size_mb: 50, price_featured_ad: 10.00,
		vat_percent: 23
	});
	const OTHER = '__other__';
	const API_BASE = process.env.REACT_APP_BACKEND_URL;

  useDocumentMeta({
    title: horse ? `${horse.title} | Equi-Market` : 'Equi-Market',
    description: horse?.description?.slice(0, 200),
    image: horse?.images?.[0] ? (horse.images[0].startsWith('http') ? horse.images[0] : `${API_BASE}${horse.images[0]}`) : '',
    url: horse ? `${window.location.origin}/horses/${horse.slug || horse.id}` : '',
    price: horse?.price,
    currency: 'EUR',
    type: 'horse'
  });

  useEffect(() => {
		breedsAPI
			.list()
			.then(r => setBreedMap(createTranslationMap(r.data || [], i18n.language)))
			.catch(() => {});
		coatsAPI
			.list()
			.then(r => setCoatMap(createTranslationMap(r.data || [], i18n.language)))
			.catch(() => {});
  }, [i18n.language]);

  useEffect(() => {
    setLoading(true);
		publicAPI
			.settings()
			.then(r => setPricing(prev => ({ ...prev, ...r.data })))
			.catch(() => {});
		horseAPI.get(id).then(r => {
			setHorse(r.data);
			if (user) {
				favoriteAPI
					.check(r.data.id)
					.then(f => setIsFav(f.data.is_favorite))
					.catch(() => {});
			}
		})
			.catch(() => navigate('/horses'))
			.finally(() => setLoading(false));
  }, [id, user, navigate]);

  const calculateAge = (dob) => {
    if (!dob) return null;
    return Math.floor((new Date() - new Date(dob)) / (365.25 * 24 * 60 * 60 * 1000));
  };

  if (loading) return (
    <div className="pt-24 min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#1A3C34] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!horse) return null;

  const hiddenImages = horse.hidden_images || [];
  const hiddenVideos = horse.hidden_videos || [];
  const images = (horse.images || []).filter(url => !hiddenImages.includes(url)).map((url, i) => ({
		url,
		preview: url.startsWith('/') ? `${ API_BASE }${ url }` : url,
		thumbnail: horse.thumbnails?.[i] ? (horse.thumbnails[i].startsWith('/') ? `${ API_BASE }${ horse.thumbnails[i] }` : horse.thumbnails[i]) : null
	}));
  const videos = (horse.videos || []).filter(url => !hiddenVideos.includes(url)).map(url => ({
		url,
		preview: url.startsWith('/') ? `${ API_BASE }${ url }` : url,
		name: url.split('/').pop()
	}));
  const hasLocation = horse.location_lat && horse.location_lng;
	const getGender = gender => {
		if (gender === 'mare') return t('horse.mare');
		if (gender === 'gelding') return t('horse.gelding');

		return t('horse.stallion');
	}

  return (
    <div className="pt-20 md:pt-24 min-h-screen" data-testid="horse-detail-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Breadcrumb */}
				<BackLink title={horse.title} uri="/horses" />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left - Images & Content */}
          <div className="lg:col-span-2 space-y-4" data-testid="horse-gallery">
            {/* Main Image */}
						<ImageViewer images={images} title={horse.title} is_featured={horse.is_featured} />

						{ videos.length > 0 &&
							<VideoViewer videos={videos} />
						}

            {/* Description */}
            <div className="bg-white border border-[#E6E2D6] rounded-sm p-6 mt-6">
              <h2 className="text-xl font-semibold text-[#1A3C34] mb-4 font-['Playfair_Display']">
								{t('horse.description')}
							</h2>
              <p className="text-[#666055] leading-relaxed whitespace-pre-wrap">{horse.description}</p>
            </div>

            {/* Details Grid */}
            <div className="bg-white border border-[#E6E2D6] rounded-sm p-6">
              <h2 className="text-xl font-semibold text-[#1A3C34] mb-4 font-['Playfair_Display']">
								{t('horse.details')}
							</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                { horse.breed &&
									<DetailItem label={t('horse.breed')}
															value={horse.breed === OTHER ? horse.customBreed : breedMap[horse.breed] || horse.breed} />
								}
                { horse.gender &&
									<DetailItem label={t('horse.gender')} value={getGender(horse.gender)} />
								}
                { horse.date_of_birth &&
									<DetailItem label={t('horse.age')}
															value={`${calculateAge(horse.date_of_birth)} ${t('horse.years')}`} />
								}
                { horse.height &&
									<DetailItem label={t('horse.height')} value={`${horse.height} ${t('horse.cm')}`} />
								}
                { horse.weight && <
									DetailItem label={t('horse.weight')} value={`${horse.weight} ${t('horse.weight_unit')}`} />
								}
                { horse.color &&
									<DetailItem label={t('horse.color')}
															value={horse.color === OTHER ? horse.customColor : coatMap[horse.color] || horse.color } />
								}
                <DetailItem label={t('horse.travel_docs')} value={horse.travel_documents ? 'Yes' : 'No'} />
                { horse.microchip_id &&
									<DetailItem label={t('horse.microchip_id')} value={horse.microchip_id} />
								}
                { horse.category &&
									<DetailItem label={t('horse.category')}
															value={horse.category === 'sale' ? t('horse.sale') : t('horse.stallion')} />
								}
              </div>
              { horse.health_issues && (
                <div className="mt-4 p-3 bg-[#F2F0E9] rounded-sm">
                  <p className="text-xs font-semibold text-[#666055] uppercase mb-1">
										{t('horse.health_issues')}
									</p>
                  <p className="text-sm text-[#1A3C34]">{horse.health_issues}</p>
                </div>
              )}
              {horse.stud_book_link && (
                <div className="mt-4">
                  <a href={horse.stud_book_link} target="_blank" rel="noopener noreferrer"
										 className="text-sm text-[#8B4513] hover:underline">{t('horse.stud_book')}</a>
                </div>
              )}
            </div>

            {/* Map */}
            { hasLocation &&
							<LocationViewer title={horse.title}
															location_name={horse.location_name}
															location_lat={horse.location_lat}
															location_lng={horse.location_lng} />
						}
          </div>

          {/* Right - Sticky Info */}
					<StickyInfoCard ad={horse}
													adType="horse"
													is_favorite={isFav}
													pricing={pricing} />
				</div>
			</div>
    </div>
  );
}

function DetailItem({ label, value }) {
  return (
    <div className="p-3 bg-[#F9F7F2] rounded-sm">
      <p className="text-[10px] font-bold text-[#666055] uppercase tracking-wider mb-1">{label}</p>
      <p className="text-sm font-semibold text-[#1A3C34] capitalize">{value}</p>
    </div>
  );
}
